import { ScrollArea } from "@devhop/ui";
import { useMemo, useState } from "react";
import type { UserWithAssignmentDTO } from "@/modules/assign-position/domain/contracts";
import type { PositionDTO } from "@/modules/positions/domain/contracts";
import { DroppablePositionCard } from "./DroppablePositionCard";

type PositionStructureProps = {
  positions: PositionDTO[];
  employees: UserWithAssignmentDTO[];
  isLoading: boolean;
  onDrop: (userId: string, positionId: string) => void;
  canManage: boolean;
};

type PositionWithEmployees = PositionDTO & {
  assignedEmployees: UserWithAssignmentDTO[];
  children: PositionWithEmployees[];
};

export function PositionStructure({
  positions,
  employees,
  isLoading,
  onDrop,
  canManage,
}: PositionStructureProps) {
  const [hoveredPositionId, setHoveredPositionId] = useState<string | null>(
    null,
  );

  // Build position tree with assigned employees
  const positionTree = useMemo(() => {
    // Create map of positions
    const positionMap = new Map<string, PositionWithEmployees>();
    positions.forEach((pos) => {
      positionMap.set(pos.id, {
        ...pos,
        assignedEmployees: [],
        children: [],
      });
    });

    // Assign employees to positions
    employees.forEach((emp) => {
      if (emp.currentPositionId) {
        const pos = positionMap.get(emp.currentPositionId);
        if (pos) {
          pos.assignedEmployees.push(emp);
        }
      }
    });

    // Build parent-child relationships
    const roots: PositionWithEmployees[] = [];
    positionMap.forEach((pos) => {
      if (pos.parentId) {
        const parent = positionMap.get(pos.parentId);
        if (parent) {
          parent.children.push(pos);
        } else {
          roots.push(pos);
        }
      } else {
        roots.push(pos);
      }
    });

    // Sort children by level
    const sortChildren = (pos: PositionWithEmployees) => {
      pos.children.sort((a, b) => a.level - b.level);
      pos.children.forEach(sortChildren);
    };
    roots.forEach(sortChildren);
    roots.sort((a, b) => a.level - b.level);

    return roots;
  }, [positions, employees]);

  const renderPosition = (pos: PositionWithEmployees, level = 0) => {
    const isHovered = hoveredPositionId === pos.id;

    return (
      <div key={pos.id} className="relative">
        <DroppablePositionCard
          position={pos}
          level={level}
          canManage={canManage}
          onDrop={onDrop}
          isHovered={isHovered}
          onMouseEnter={() => setHoveredPositionId(pos.id)}
          onMouseLeave={() => setHoveredPositionId(null)}
        />

        {/* Children */}
        {pos.children.length > 0 && (
          <div className="ml-4 border-border border-l pl-2">
            {pos.children.map((child) => renderPosition(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">ກໍາລັງໂຫຼດ...</span>
      </div>
    );
  }

  if (positionTree.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">ບໍ່ມີຕໍາແໜ່ງ</span>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-16rem)] p-4">
      {positionTree.map((pos) => renderPosition(pos))}
    </ScrollArea>
  );
}
